<template>
	<div>
		<PageHeader
			:data="{
				name: 'Server Monitoring',
				breadcrumbs: [{ name: 'Home', to: '/' }],
			}"
		/>

		<div class="row">
			<div class="col-12">
				<!-- Server Performance Accordion -->
				<div class="accordion mb-4" id="serverPerformanceAccordion">
					<div class="accordion-item">
						<h2 class="accordion-header" id="serverPerformanceHeading">
							<button
								class="accordion-button collapsed"
								type="button"
								data-bs-toggle="collapse"
								data-bs-target="#serverPerformanceCollapse"
								aria-expanded="false"
								aria-controls="serverPerformanceCollapse"
								@click="loadServerPerformance"
							>
								<font-awesome-icon :icon="['fas', 'chart-line']" class="me-2" />
								Server Performance Metrics
								<span class="badge bg-secondary ms-2">{{ Object.keys(serverPerformance).length }} Servers</span>
							</button>
						</h2>
						<div
							id="serverPerformanceCollapse"
							class="accordion-collapse collapse"
							aria-labelledby="serverPerformanceHeading"
							data-bs-parent="#serverPerformanceAccordion"
						>
							<div class="accordion-body">
								<div v-if="performanceLoading" class="text-center py-4">
									<font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin fa-2x text-primary mb-3" />
									<p class="text-muted">Loading server performance data...</p>
								</div>
								<div v-else-if="Object.keys(serverPerformance).length === 0" class="text-center py-4">
									<font-awesome-icon :icon="['fas', 'info-circle']" class="fa-2x text-muted mb-3" />
									<p class="text-muted">No performance data available</p>
								</div>
								<div v-else class="row">
									<div v-for="(performance, serverName) in sortedServerPerformance" :key="serverName" class="col-md-3 mb-3">
										<div class="card border-0 shadow-sm">
											<div class="card-body text-center p-3">
												<div class="server-performance-indicator" :class="getPingColorClass(performance.avgPing)">
													<h4 class="mb-1">{{ serverName }}</h4>
													<div class="ping-display">
														<span class="ping-value">{{ Math.round(performance.avgPing) }}</span>
														<small class="ping-unit">ms</small>
													</div>
													<small class="text-muted d-block mt-1">
														{{ performance.count }} website{{ performance.count !== 1 ? 's' : '' }}
													</small>
												</div>
												<div class="mt-3">
													<button
														class="btn btn-danger btn-sm"
														@click="rebootServer(serverName)"
														:disabled="loading"
													>
														<font-awesome-icon :icon="['fas', 'power-off']" class="me-1" />
														Reboot
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="mt-3">
									<div class="d-flex justify-content-center gap-4">
										<div class="legend-item">
											<span class="legend-dot bg-success"></span>
											<small class="text-muted">0-1000ms (Excellent)</small>
										</div>
										<div class="legend-item">
											<span class="legend-dot bg-warning"></span>
											<small class="text-muted">1001-3500ms (Good)</small>
										</div>
										<div class="legend-item">
											<span class="legend-dot bg-danger"></span>
											<small class="text-muted">3500ms+ (Slow)</small>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="card">
					<div v-if="!error" class="card-body">
						<!-- Status Overview Cards -->
						<div class="row mb-4 justify-content-center">
							<div class="col-md-4">
								<div
									class="card border-success status-card"
									:class="{ 'active': statusFilter === 'online' }"
									@click="toggleStatusFilter('online')"
									role="button"
								>
									<div class="card-body text-center">
										<h2 class="text-success mb-2">{{ statusSummary.online_servers }}</h2>
										<p class="mb-0 text-success">Online Websites</p>
										<small class="text-muted">Click to filter</small>
									</div>
								</div>
							</div>
							<div class="col-md-4">
								<div
									class="card border-warning status-card"
									:class="{ 'active': statusFilter === 'offline' }"
									@click="toggleStatusFilter('offline')"
									role="button"
								>
									<div class="card-body text-center">
										<h2 class="text-warning mb-2">{{ statusSummary.offline_servers }}</h2>
										<p class="mb-0 text-warning">Offline Websites</p>
										<small class="text-muted">Click to filter</small>
									</div>
								</div>
							</div>
						</div>

						<!-- Action Buttons -->
						<div class="actions d-flex mb-4 justify-content-between align-items-center">
							<div class="d-flex align-items-center">
								<h5 class="mb-0 me-3">Server List</h5>
								<span class="badge bg-secondary">
									Last Updated: {{ lastUpdated }}
								</span>
							</div>
							<div>
								<button @click="refreshData" class="btn btn-outline-primary me-2" :disabled="loading">
									<font-awesome-icon
										:icon="['fas', 'sync']"
										:class="{ 'fa-spin': loading }"
										class="me-1"
									/>
									Refresh
								</button>
								<button @click="toggleAutoRefresh" class="btn" :class="autoRefresh ? 'btn-success' : 'btn-outline-success'">
									<font-awesome-icon :icon="['fas', autoRefresh ? 'pause' : 'play']" class="me-1" />
									Auto Refresh
								</button>
							</div>
						</div>

						<!-- Server List Table -->
						<div class="table-responsive">
							<table :class="!loading ? 'table table-hover' : 'table table-hover loading'">
								<thead>
									<tr>
										<th width="80">Status</th>
										<th>Website</th>
										<th>Server</th>
										<th>Type</th>
										<th>URL/IP</th>
										<th>Response Time</th>
										<th>Last Check</th>
										<th>Error</th>
										<th width="100">Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="server in filteredServers" :key="server.server_id" :class="getRowClass(server)">
											<td class="text-center">
												<div class="status-indicator" :class="getStatusClass(server)">
													<font-awesome-icon :icon="getStatusIcon(server)" />
												</div>
											</td>
											<td>
												<div class="d-flex flex-column">
													<span class="fw-bold">{{ server.label }}</span>
													<small class="text-muted">ID: {{ server.server_id }}</small>
												</div>
											</td>
											<td class="text-center">
												<span v-if="extractServerName(server)" class="badge bg-secondary">
													{{ extractServerName(server) }}
												</span>
												<span v-else class="text-muted">—</span>
											</td>
											<td>
												<span class="badge" :class="getTypeClass(server.type)">
													{{ server.type.toUpperCase() }}
												</span>
											</td>
											<td>
												<span>{{ server.ip }}</span>
											</td>
											<td>
												<span v-if="server.response_time" class="badge bg-light text-dark">
													{{ Math.round(server.response_time * 1000) }}ms
												</span>
												<span v-else class="text-muted">—</span>
											</td>
											<td>
												<div class="d-flex flex-column">
													<span>{{ formatDate(server.last_check) }}</span>
													<small class="text-muted">{{ timeAgo(server.last_check) }}</small>
												</div>
											</td>
											<td>
												<div v-if="server.error" class="error-cell">
													<span class="text-danger" :title="server.error">
														{{ truncateError(server.error) }}
													</span>
												</div>
												<span v-else class="text-muted">—</span>
											</td>
											<td>
												<div class="btn-group btn-group-sm">
													<button
														class="btn btn-outline-primary btn-sm"
														@click="viewServerDetails(server)"
														title="View Details"
													>
														<font-awesome-icon :icon="['fas', 'eye']" />
													</button>
													<button
														class="btn btn-outline-success btn-sm"
														@click="testServer(server)"
														title="Test Now"
														:disabled="loading"
													>
														<font-awesome-icon :icon="['fas', 'play']" />
													</button>
												</div>
											</td>
										</tr>
								</tbody>
							</table>
						</div>

						<!-- Loading State -->
						<div v-if="loading && servers.length === 0" class="text-center py-4">
							<font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin fa-2x text-primary mb-3" />
							<p class="text-muted">Loading server data...</p>
						</div>

						<!-- Empty State -->
						<div v-if="!loading && servers.length === 0" class="text-center py-4">
							<font-awesome-icon :icon="['fas', 'server']" class="fa-3x text-muted mb-3" />
							<p class="text-muted">No servers found</p>
						</div>
					</div>

					<!-- Error State -->
					<div v-else class="card-body">
						<div class="alert alert-danger" role="alert">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-2" />
							{{ error }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import API from '../../api/index'
import PageHeader from '../../components/layout/PageHeader.vue'

export default {
	name: 'ServerMonitoring',
	components: {
		PageHeader
	},
	data() {
		return {
			servers: [],
			statusSummary: {
				total_servers: 0,
				active_servers: 0,
				online_servers: 0,
				offline_servers: 0
			},
			serverPerformance: {},
			statusFilter: null, // 'online', 'offline', or null for all
			loading: false,
			performanceLoading: false,
			performanceLoaded: false,
			error: null,
			autoRefresh: false,
			refreshInterval: null,
			lastUpdated: null
		}
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),

		sortedServerPerformance() {
			// Convert object to array, sort by avgPing (worst to best), then back to object
			const sorted = Object.entries(this.serverPerformance)
				.sort(([, a], [, b]) => b.avgPing - a.avgPing) // Sort descending (worst first)
				.reduce((obj, [key, value]) => {
					obj[key] = value
					return obj
				}, {})

			return sorted
		},

		filteredServers() {
			if (!this.statusFilter) {
				return this.servers
			}

			return this.servers.filter(server => {
				if (this.statusFilter === 'online') {
					return server.status === 'on'
				} else if (this.statusFilter === 'offline') {
					return server.status === 'off'
				}
				return true
			})
		}
	},
	mounted() {
		this.loadData()
		this.lastUpdated = this.getCurrentTime()
	},
	beforeDestroy() {
		this.stopAutoRefresh()
	},
	methods: {
		async loadData() {
			this.loading = true
			this.error = null

			try {
				// Load both summary and server list in parallel
				const [summaryResponse, serversResponse] = await Promise.all([
					API.get('/phpmon/status-summary'),
					API.get('/phpmon/servers')
				])

				if (summaryResponse.data?.success) {
					this.statusSummary = summaryResponse.data.data
				}

				if (serversResponse.data?.success) {
					this.servers = serversResponse.data.data
				}

				this.lastUpdated = this.getCurrentTime()

			} catch (error) {
				console.error('Error loading monitoring data:', error)
				this.error = `Failed to load monitoring data: ${error.message}`
			} finally {
				this.loading = false
			}
		},

		async refreshData() {
			await this.loadData()

			this.$swal.fire({
				toast: true,
				position: 'top-end',
				icon: 'success',
				title: 'Data refreshed successfully',
				showConfirmButton: false,
				timer: 2000
			})
		},

		toggleAutoRefresh() {
			this.autoRefresh = !this.autoRefresh

			if (this.autoRefresh) {
				this.refreshInterval = setInterval(() => {
					this.loadData()
				}, 30000) // Refresh every 30 seconds
			} else {
				this.stopAutoRefresh()
			}
		},

		stopAutoRefresh() {
			if (this.refreshInterval) {
				clearInterval(this.refreshInterval)
				this.refreshInterval = null
			}
			this.autoRefresh = false
		},

		getStatusClass(server) {
			if (server.status === 'on') return 'status-online'
			return 'status-offline'
		},

		getStatusIcon(server) {
			if (server.status === 'on') return ['fas', 'check-circle']
			return ['fas', 'times-circle']
		},

		getRowClass(server) {
			if (server.status === 'off') return 'table-danger'
			return ''
		},

		getTypeClass(type) {
			switch (type.toLowerCase()) {
				case 'website': return 'bg-primary'
				case 'service': return 'bg-info'
				case 'ping': return 'bg-secondary'
				default: return 'bg-secondary'
			}
		},

		extractServerName(server) {
			const text = server.label || server.ip || ''

			// Look for VPS2-VPS9, JP, and NODNS1 server patterns in various formats
			const patterns = [
				/VPS[2-9]/i,           // VPS2, VPS3, etc.
				/\bJP\b/i,             // JP (standalone)
				/NODNS[1-9]/i,         // NODNS1, etc.
				/vps[2-9]/i,           // lowercase vps
				/nodns[1-9]/i,         // lowercase nodns
				/VPS[2-9](?=\.)/i,     // VPS followed by dot
				/NODNS[1-9](?=\.)/i,   // NODNS followed by dot
				/[^a-zA-Z]VPS[2-9]/i,  // VPS with non-letter before
				/[^a-zA-Z]NODNS[1-9]/i,// NODNS with non-letter before
				/VPS[2-9][^a-zA-Z]/i,  // VPS with non-letter after
				/NODNS[1-9][^a-zA-Z]/i // NODNS with non-letter after
			]

			for (const pattern of patterns) {
				const match = text.match(pattern)
				if (match) {
					return match[0].replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
				}
			}

			return null
		},

		formatDate(dateString) {
			if (!dateString) return '—'
			return new Date(dateString).toLocaleString()
		},

		timeAgo(dateString) {
			if (!dateString) return ''
			const diff = Date.now() - new Date(dateString).getTime()
			const minutes = Math.floor(diff / 60000)

			if (minutes < 1) return 'Just now'
			if (minutes < 60) return `${minutes}m ago`

			const hours = Math.floor(minutes / 60)
			if (hours < 24) return `${hours}h ago`

			const days = Math.floor(hours / 24)
			return `${days}d ago`
		},

		truncateError(error) {
			if (!error) return ''
			return error.length > 50 ? error.substring(0, 50) + '...' : error
		},

		getCurrentTime() {
			return new Date().toLocaleTimeString()
		},

		viewServerDetails(server) {
			// TODO: Implement server details modal or page
			this.$swal.fire({
				title: `Server Details: ${server.label}`,
				html: `
					<div class="text-start">
						<p><strong>ID:</strong> ${server.server_id}</p>
						<p><strong>Type:</strong> ${server.type}</p>
						<p><strong>URL/IP:</strong> ${server.ip}</p>
						<p><strong>Status:</strong> ${server.status}</p>
						<p><strong>Response Time:</strong> ${server.response_time ? Math.round(server.response_time * 1000) + 'ms' : '—'}</p>
						<p><strong>Last Check:</strong> ${this.formatDate(server.last_check)}</p>
						${server.error ? `<p><strong>Error:</strong> ${server.error}</p>` : ''}
					</div>
				`,
				icon: server.status === 'on' ? 'success' : 'error',
				confirmButtonText: 'Close',
				width: '500px'
			})
		},

		async testServer(server) {
			// TODO: Implement server test functionality
			this.$swal.fire({
				toast: true,
				position: 'top-end',
				icon: 'info',
				title: `Testing ${server.label}...`,
				text: 'Server test functionality will be implemented soon',
				showConfirmButton: false,
				timer: 3000
			})
		},

		async loadServerPerformance() {
			// Only load once per session unless explicitly refreshed
			if (this.performanceLoaded && Object.keys(this.serverPerformance).length > 0) {
				return
			}

			this.performanceLoading = true

			try {
				// Calculate performance from current server data
				const serverStats = {}

				this.servers.forEach(server => {
					const serverName = this.extractServerName(server)

					if (serverName && server.response_time && server.status === 'on') {
						if (!serverStats[serverName]) {
							serverStats[serverName] = {
								totalPing: 0,
								count: 0,
								avgPing: 0
							}
						}

						const pingMs = server.response_time * 1000 // Convert to milliseconds
						serverStats[serverName].totalPing += pingMs
						serverStats[serverName].count += 1
					}
				})

				// Calculate averages
				Object.keys(serverStats).forEach(serverName => {
					const stats = serverStats[serverName]
					stats.avgPing = stats.totalPing / stats.count
				})

				this.serverPerformance = serverStats
				this.performanceLoaded = true

			} catch (error) {
				console.error('Error loading server performance:', error)
				this.$swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'error',
					title: 'Failed to load performance data',
					showConfirmButton: false,
					timer: 3000
				})
			} finally {
				this.performanceLoading = false
			}
		},

		getPingColorClass(avgPing) {
			if (avgPing <= 1000) {
				return 'ping-excellent' // Green
			} else if (avgPing <= 3500) {
				return 'ping-good' // Yellow
			} else {
				return 'ping-slow' // Red
			}
		},

		toggleStatusFilter(status) {
			if (this.statusFilter === status) {
				// If clicking the same filter, clear it
				this.statusFilter = null
			} else {
				// Set new filter
				this.statusFilter = status
			}
		},

		async rebootServer(serverName) {
			// First confirmation
			const firstConfirm = await this.$swal.fire({
				title: `Reboot ${serverName}?`,
				text: 'This will restart the server and may cause temporary downtime.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc3545',
				cancelButtonColor: '#6c757d',
				confirmButtonText: 'Continue',
				cancelButtonText: 'Cancel',
				toast: false,
				position: 'center',
				timer: null,
				timerProgressBar: false,
				allowOutsideClick: true,
				allowEscapeKey: true,
				showCloseButton: true
			})

			if (!firstConfirm.isConfirmed) {
				return
			}

			// Second confirmation (more serious)
			const secondConfirm = await this.$swal.fire({
				title: 'Are you absolutely sure?',
				html: `
					<div class="text-start">
						<p class="mb-2"><strong>Server:</strong> ${serverName}</p>
						<p class="mb-2"><strong>Action:</strong> Server Reboot</p>
						<hr>
						<p class="text-danger mb-0">
							<i class="fas fa-exclamation-triangle me-2"></i>
							This action cannot be undone and will cause service interruption!
						</p>
					</div>
				`,
				icon: 'error',
				showCancelButton: true,
				confirmButtonColor: '#dc3545',
				cancelButtonColor: '#28a745',
				confirmButtonText: 'Yes, Reboot Server!',
				cancelButtonText: 'No, Keep Running',
				toast: false,
				position: 'center',
				timer: null,
				timerProgressBar: false,
				allowOutsideClick: false,
				allowEscapeKey: false,
				showCloseButton: false,
				reverseButtons: true
			})

			if (secondConfirm.isConfirmed) {
				// TODO: Implement actual reboot functionality when user defines the method
				this.$swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'info',
					title: `Reboot request for ${serverName}`,
					text: 'Server reboot functionality will be implemented by user requirements',
					showConfirmButton: false,
					timer: 5000
				})
			}
		}
	}
}
</script>

<style scoped>
.status-indicator {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
}

.status-online {
	background-color: #d4edda;
	color: #155724;
}

.status-offline {
	background-color: #f8d7da;
	color: #721c24;
}

.error-cell {
	max-width: 200px;
}

.table.loading {
	opacity: 0.6;
	pointer-events: none;
}

.table-danger td {
	background-color: rgba(220, 53, 69, 0.1) !important;
}

.badge {
	font-size: 0.75em;
}

.btn-group-sm .btn {
	padding: 0.25rem 0.5rem;
	font-size: 0.75rem;
}

/* Server Performance Accordion Styles */
.accordion-button {
	background-color: #f8f9fa;
	color: #495057;
}

.accordion-button:not(.collapsed) {
	background-color: #e3f2fd;
	color: #1976d2;
}

.server-performance-indicator {
	padding: 15px;
	border-radius: 8px;
	transition: all 0.3s ease;
}

.ping-display {
	display: flex;
	align-items: baseline;
	justify-content: center;
	gap: 2px;
}

.ping-value {
	font-size: 2rem;
	font-weight: 700;
	line-height: 1;
}

.ping-unit {
	font-size: 0.9rem;
	font-weight: 500;
	opacity: 0.8;
}

/* Color coding for ping ranges */
.ping-excellent {
	background-color: rgba(25, 135, 84, 0.1);
	border: 2px solid rgba(25, 135, 84, 0.3);
	color: #198754;
}

.ping-good {
	background-color: rgba(255, 193, 7, 0.1);
	border: 2px solid rgba(255, 193, 7, 0.3);
	color: #fd7e14;
}

.ping-slow {
	background-color: rgba(220, 53, 69, 0.1);
	border: 2px solid rgba(220, 53, 69, 0.3);
	color: #dc3545;
}

/* Legend styles */
.legend-item {
	display: flex;
	align-items: center;
	gap: 8px;
}

.legend-dot {
	width: 12px;
	height: 12px;
	border-radius: 50%;
}

/* Auto refresh indicator */
.btn-success {
	position: relative;
}

.btn-success.auto-refresh::after {
	content: '';
	position: absolute;
	top: 0;
	right: 0;
	width: 8px;
	height: 8px;
	background-color: #28a745;
	border: 2px solid white;
	border-radius: 50%;
	animation: pulse 1s infinite;
}

@keyframes pulse {
	0% {
		box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
	}
	70% {
		box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
	}
	100% {
		box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
	}
}

/* Status Card Styles */
.status-card {
	cursor: pointer;
	transition: all 0.3s ease;
	transform: scale(1);
}

.status-card:hover {
	transform: scale(1.02);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.status-card.active {
	transform: scale(1.05);
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
	border-width: 3px !important;
}
</style>